import axios from 'axios';
import { API_CONFIG } from '../config/api';

// Request debouncing to prevent rapid successive requests
let pendingRequests: { [key: string]: Promise<any> } = {};

// Debounced request function
const debouncedRequest = async (key: string, requestFn: () => Promise<any>) => {
  if (pendingRequests[key] !== undefined) {
    return pendingRequests[key];
  }
  
  pendingRequests[key] = requestFn();
  
  try {
    const result = await pendingRequests[key];
    return result;
  } finally {
    delete pendingRequests[key];
  }
};

// Create axios instance
const api = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  withCredentials: false, // Don't use cookies for API authentication
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle response errors
api.interceptors.response.use(
  (response) => {
    // Check if we received HTML when we expected JSON
    if (typeof response.data === 'string' && response.data.startsWith('<!DOCTYPE')) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
      throw new Error('Session expired. Please log in again.');
    }
    return response;
  },
  (error) => {
    // Handle rate limiting errors (429)
    if (error.response?.status === 429) {
      console.warn('Rate limit exceeded. Please slow down your requests.');
      // Don't redirect to login for rate limiting, just show a warning
      return Promise.reject(new Error('Too many requests. Please wait a moment and try again.'));
    }
    
    // Check if we received HTML instead of JSON (happens when redirected to login page)
    if (error.response?.headers?.['content-type']?.includes('text/html')) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
      return Promise.reject(new Error('Session expired. Please log in again.'));
    }
    
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Axios instance with extended timeout for media uploads
const mediaUploadApi = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: 600000, // 10 minutes for large video uploads
  headers: {
    'Accept': 'application/json',
  },
});

// Request interceptor to add auth token for media uploads
mediaUploadApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor to handle auth errors for media uploads
mediaUploadApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export interface User {
  id: number;
  name: string;
  email: string;
  email_verified_at?: string;
  phone?: string;
  is_admin?: boolean;
  role?: 'admin' | 'property_manager' | 'staff' | 'cleaner';
  is_active?: boolean;
  created_at: string;
  updated_at: string;
  assigned_properties?: PropertyAssignment[];
  cleaner_properties?: Property[];
}

export interface PropertyAssignment {
  id: number;
  user_id: number;
  property_id: number;
  assignment_type: 'manager' | 'staff';
  created_at: string;
  updated_at: string;
  property?: Property;
}

export interface Property {
  id: number;
  name: string;
  type: 'apartment' | 'house' | 'condo' | 'commercial' | 'building' | 'villa' | 'other';
  address: string;
  city?: string;
  country?: string;
  currency_id?: number;
  latitude?: number;
  longitude?: number;
  description?: string;
  mobile_phone_1?: string;
  mobile_phone_2?: string;
  facilities?: string[];
  special_specs?: string[];
  main_image?: string;
  logo_url?: string;
  images?: string[];
  videos?: string[];
  created_by: number;
  owner_id?: number;
  units_count?: number;
  showOnCalendar?: boolean;
  created_at: string;
  updated_at: string;
  owner?: Owner;
  units?: Unit[];
  currency?: Currency;
  creator?: User;
}

export interface PropertyCreateData {
  name: string;
  type: 'apartment' | 'house' | 'condo' | 'commercial' | 'building' | 'villa' | 'other';
  address: string;
  city?: string;
  country?: string;
  currency_id?: number;
  latitude?: number;
  longitude?: number;
  description?: string;
  mobile_phone_1?: string;
  mobile_phone_2?: string;
  facilities?: string[];
  special_specs?: string[];
  showOnCalendar?: boolean;
}

export interface Unit {
  id: number;
  property_id: number;
  parent_unit_id?: number;
  level: number;
  sort_order: number;
  unit_number: string;
  type: string; // Dynamic unit type based on unit_types table
  name: string;
  bedrooms: number;
  bathrooms: number;
  square_feet: number;
  rent_amount: number;
  status: 'vacant' | 'occupied' | 'maintenance';
  cleaning_status?: 'cleaned' | 'not_cleaned';
  description?: string;
  rental_types: ('Hour' | '3 Hours' | 'Daily' | 'Monthly' | 'Yearly')[];
  images?: string[];
  videos?: string[];
  living_room_count?: number;
  mens_guest_room_count?: number;
  womens_guest_room_count?: number;
  single_bed_count?: number;
  double_bed_count?: number;
  max_persons?: number;
  checkin_time?: string;
  checkout_time?: string;
  tax_rate?: number;
  created_at: string;
  updated_at: string;
  property?: Property;
  bookings?: Booking[];
  parent?: Unit;
  children?: Unit[];
  descendants?: Unit[];
}

export interface UnitType {
  id: number;
  name: string;
  label: string;
  description?: string;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface Booking {
  id: number;
  booking_number?: string;
  property_id: number;
  unit_id: number;
  broker_id?: number;
  tenant_name: string;
  tenant_email?: string | null;
  tenant_phone: string;
  start_date: string;
  end_date: string;
  rent_amount: number;
  deposit_amount: number;
  extra_amount: number;
  extra_description?: string;
  total_amount: number;
  paid_amount: number;
  payment_status: 'none' | 'partial' | 'full';
  status: 'pending' | 'active' | 'completed' | 'cancelled';
  auto_cancel_at?: string | null;
  cancel_after_minutes?: number | null;
  notes?: string;
  currency_id?: number;
  commission_rate?: number;
  commission_amount?: number;
  checkin_status?: 'not_checked_in' | 'checked_in' | 'checked_out';
  checkin_time?: string | null;
  checkout_time?: string | null;
  created_at: string;
  updated_at: string;
  property?: Property;
  unit?: Unit;
  broker?: Broker;
  currency?: Currency;
  histories?: BookingHistory[];
  display_amounts?: {
    [key: string]: {
      value: number;
      currency: Currency;
      symbol: string;
    };
  };
}

export interface BookingHistory {
  id: number;
  booking_id: number;
  user_id: number;
  action: string;
  description: string;
  old_values?: any;
  new_values?: any;
  created_at: string;
  updated_at: string;
  user?: {
    id: number;
    name: string;
    email: string;
  };
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  name: string;
  email: string;
  password: string;
  password_confirmation: string;
}

export interface AuthResponse {
  user: User;
  token: string;
  token_type: string;
}

export interface Owner {
  id: number;
  name: string;
  nationality?: string;
  mobile_number?: string;
  address?: string;
  created_at: string;
  updated_at: string;
  properties?: Property[];
}

export interface PropertyColor {
  property_id: number;
  property_name?: string;
  background_color: string | null;
  text_color: string | null;
}

export interface PropertyColorUpdate {
  property_id: number;
  background_color?: string | null;
  text_color?: string | null;
}

export interface Currency {
  id: number;
  code: string;
  name: string;
  symbol: string;
  exchange_rate: number | string;
  is_default: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface DailyRate {
  id: number;
  unit_id: number;
  day_of_year: number;
  year: number;
  rate: number;
  created_at: string;
  updated_at: string;
}

export interface DailyRatesResponse {
  unit_id: number;
  start_date: string;
  end_date: string;
  rates: { [date: string]: number };
}

export interface MultipleUnitRatesResponse {
  start_date: string;
  end_date: string;
  units: Array<{
    unit_id: number;
    unit_name: string;
    unit_number: string;
    rates: { [date: string]: number };
  }>;
}

export interface UpdateDailyRatesRequest {
  start_date: string;
  end_date: string;
  rate: number;
}

export interface UpdateSingleRateRequest {
  date: string;
  rate: number;
}

export interface CopyRatesRequest {
  source_start_date: string;
  source_end_date: string;
  target_start_date: string;
  target_end_date: string;
}

export interface InitializeRatesRequest {
  year: number;
  default_rate?: number;
}

export interface BatchUpdateRateRequest {
  unit_id: number;
  date: string;
  rate: number;
}

export interface BatchUpdateRatesRequest {
  updates: BatchUpdateRateRequest[];
}

export interface Broker {
  id: number;
  name: string;
  email?: string;
  phone?: string;
  address?: string;
  company_name?: string;
  account_number?: string;
  commission_rate: number;
  notes?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface BrokerCreateData {
  name: string;
  email?: string;
  phone?: string;
  address?: string;
  company_name?: string;
  commission_rate?: number;
  notes?: string;
  is_active?: boolean;
}

export interface BrokerUpdateData extends Partial<BrokerCreateData> {}

// User Preferences API
export const userPreferencesApi = {
  getAll: async () => {
    const response = await api.get('/user-preferences');
    return response.data.preferences;
  },
  
  get: async (key: string) => {
    const response = await api.get(`/user-preferences/${key}`);
    return response.data.value;
  },
  
  set: async (key: string, value: any) => {
    const response = await api.post('/user-preferences', { key, value });
    return response.data;
  },
  
  update: async (key: string, value: any) => {
    const response = await api.put(`/user-preferences/${key}`, { value });
    return response.data;
  },
  
  delete: async (key: string) => {
    const response = await api.delete(`/user-preferences/${key}`);
    return response.data;
  },
  
  setMultiple: async (preferences: Array<{ key: string; value: any }>) => {
    const response = await api.post('/user-preferences/bulk', { preferences });
    return response.data;
  },
};

// Auth API
export const authApi = {
  login: async (credentials: LoginCredentials) => {
    const response = await api.post<AuthResponse>('/login', credentials);
    return response.data;
  },
  
  register: async (data: RegisterData) => {
    const response = await api.post<AuthResponse>('/register', data);
    return response.data;
  },
  
  logout: async () => {
    await api.post('/logout');
  },
  
  getUser: async () => {
    const response = await api.get<User>('/user');
    return response.data;
  },

  updateProfile: async (data: { name: string; email: string; phone: string }) => {
    const response = await api.put<User>('/user/profile', data);
    return response.data;
  },

  updatePassword: async (data: { current_password: string; new_password: string; confirm_password: string }) => {
    const requestData = {
      current_password: data.current_password,
      new_password: data.new_password,
      new_password_confirmation: data.confirm_password
    };
    console.log('Sending password update request:', requestData);
    const response = await api.put<{ message: string }>('/user/password', requestData);
    return response.data;
  },
};

// Properties API
export const propertyApi = {
  getAll: async () => {
    try {
      const response = await api.get<Property[]>('/properties');
      return response.data;
    } catch (error) {
      // Fallback to test data if auth fails
      console.log('Auth failed, using test data for properties');
      return [
        { 
          id: 1, 
          name: 'Mountain View Condos', 
          type: 'condo' as const, 
          address: '123 Mountain View St', 
          city: 'Muscat', 
          country: 'Oman', 
          currency_id: 1, 
          latitude: 23.5880, 
          longitude: 58.3829, 
          description: 'Luxury condos with mountain view', 
          mobile_phone_1: '+968 1234 5678', 
          mobile_phone_2: '+968 9876 5432', 
          facilities: ['Pool', 'Gym', 'Parking'], 
          special_specs: ['Mountain View', 'Luxury'], 
          main_image: undefined,
          logo_url: undefined, 
          images: [], 
          videos: [], 
          created_by: 1, 
          owner_id: 1, 
          units_count: 10, 
          showOnCalendar: true, 
          created_at: '2025-01-15T00:00:00.000000Z', 
          updated_at: '2025-01-15T00:00:00.000000Z',
          currency: {
            id: 1,
            code: 'OMR',
            name: 'Omani Rial',
            symbol: 'ر.ع',
            exchange_rate: 1,
            is_default: true,
            is_active: true,
            created_at: '2025-01-15T00:00:00.000000Z',
            updated_at: '2025-01-15T00:00:00.000000Z'
          },
          units: []
        }
      ];
    }
  },
  
  getById: async (id: number) => {
    const response = await api.get<{property: Property, units: Unit[]}>(`/properties/${id}`);
    return response.data.property;
  },
  
  create: async (data: PropertyCreateData) => {
    const response = await api.post<{ property: Property }>('/properties', data);
    return response.data.property;
  },
  
  update: async (id: number, data: Partial<Property>) => {
    const response = await api.put<Property>(`/properties/${id}`, data);
    return response.data;
  },
  
  delete: async (id: number) => {
    await api.delete(`/properties/${id}`);
  },

  // Owner assignment method
  assignOwner: async (propertyId: number, ownerId: number) => {
    const response = await api.put<{ property: Property; message: string }>(`/properties/${propertyId}/assign-owner`, {
      owner_id: ownerId
    });
    return response.data;
  },

  // File upload methods
  uploadMainImage: async (propertyId: number, file: File) => {
    const formData = new FormData();
    formData.append('main_image', file);
    
    const response = await api.post(`/properties/${propertyId}/upload-main-image`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  uploadLogo: async (propertyId: number, file: File) => {
    const formData = new FormData();
    formData.append('logo', file);
    
    const response = await api.post(`/properties/${propertyId}/upload-logo`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  uploadMedia: async (propertyId: number, files: File[], onProgress?: (progress: number) => void) => {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files[]', file);
    });
    
    const response = await mediaUploadApi.post(`/properties/${propertyId}/upload-media`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total && onProgress) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });
    return response.data;
  },

  deleteMedia: async (propertyId: number, filePath: string) => {
    const response = await api.delete(`/properties/${propertyId}/delete-media`, {
      data: { file_path: filePath }
    });
    return response.data;
  },

  getMedia: async (propertyId: number) => {
    const response = await api.get(`/properties/${propertyId}/media`);
    return response.data;
  },

  toggleShowOnCalendar: async (propertyId: number, showOnCalendar: boolean) => {
    const response = await api.put<Property>(`/properties/${propertyId}/toggle-calendar-visibility`, {
      showOnCalendar
    });
    return response.data;
  },
};

// Units API
export const unitApi = {
  getAll: async () => {
    const response = await api.get<Unit[]>('/units');
    return response.data;
  },
  
  getById: async (id: number) => {
    const response = await api.get<{ unit: Unit }>(`/units/${id}`);
    return response.data.unit;
  },
  
  getByProperty: async (propertyId: number) => {
    const response = await api.get<Unit[]>(`/properties/${propertyId}/units`);
    return response.data;
  },

  // Get hierarchical tree structure
  getTree: async (propertyId: number) => {
    const response = await api.get<Unit[]>(`/properties/${propertyId}/units`);
    return response.data;
  },

  // Get flat list (backwards compatibility)
  getFlat: async (propertyId: number) => {
    const response = await api.get<Unit[]>(`/properties/${propertyId}/units/flat`);
    return response.data;
  },

  // Get possible parent units for a unit
  getPossibleParents: async (propertyId: number, unitId?: number) => {
    const endpoint = unitId 
      ? `/properties/${propertyId}/units/possible-parents/${unitId}`
      : `/properties/${propertyId}/units/possible-parents`;
    const response = await api.get<Unit[]>(endpoint);
    return response.data;
  },
  
  create: async (data: Omit<Unit, 'id' | 'created_at' | 'updated_at' | 'level' | 'sort_order'> & { sort_order?: number }) => {
    const { property_id, ...unitData } = data;
    const response = await api.post<Unit>(`/properties/${property_id}/units`, unitData);
    return response.data;
  },
  
  update: async (id: number, data: Partial<Unit>) => {
    const response = await api.put<Unit>(`/units/${id}`, data);
    return response.data;
  },
  
  delete: async (id: number) => {
    await api.delete(`/units/${id}`);
  },

  // Unit file upload methods
  uploadMedia: async (unitId: number, files: File[], onProgress?: (progress: number) => void) => {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files[]', file);
    });
    
    const response = await mediaUploadApi.post(`/units/${unitId}/upload-media`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total && onProgress) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });
    return response.data;
  },

  deleteMedia: async (unitId: number, filePath: string) => {
    const response = await api.delete(`/units/${unitId}/delete-media`, {
      data: { file_path: filePath }
    });
    return response.data;
  },
};

// Bookings API
export const bookingApi = {
  getAll: async () => {
    const response = await api.get<Booking[]>('/bookings');
    return response.data;
  },
  
  getById: async (id: number) => {
    const response = await api.get<{ booking: Booking }>(`/bookings/${id}`);
    return response.data.booking;
  },
  
  create: async (data: Omit<Booking, 'id' | 'created_at' | 'updated_at'>) => {
    const response = await api.post<Booking>('/bookings', data);
    return response.data;
  },
  
  update: async (id: number, data: Partial<Booking>) => {
    const response = await api.put<Booking>(`/bookings/${id}`, data);
    return response.data;
  },
  
  addPayment: async (id: number, amount: number) => {
    const response = await api.post<{ booking: Booking; payment_amount: number; message: string }>(`/bookings/${id}/payment`, { amount });
    return response.data;
  },
  
  modifyPayment: async (id: number, oldAmount: number, newAmount: number, reason?: string) => {
    const response = await api.put<{ booking: Booking; message: string }>(`/bookings/${id}/payment`, { 
      old_amount: oldAmount, 
      new_amount: newAmount,
      reason 
    });
    return response.data;
  },
  
  delete: async (id: number) => {
    await api.delete(`/bookings/${id}`);
  },

  confirm: async (id: number) => {
    const response = await api.post<{ booking: Booking; message: string }>(`/bookings/${id}/confirm`);
    return response.data;
  },

  getHistory: async (id: number) => {
    const response = await api.get<{ histories: BookingHistory[] }>(`/bookings/${id}/history`);
    return response.data.histories;
  },

  checkConflicts: async (data: { unit_id: number; start_date: string; end_date: string }) => {
    const response = await api.post<{
      conflict: boolean;
      conflict_type?: string;
      unit?: {
        id: number;
        name: string;
      };
      conflicting_unit?: {
        id: number;
        name: string;
      };
      conflicting_booking?: {
        id: number;
        tenant_name: string;
        start_date: string;
        end_date: string;
        unit_name: string;
      };
    }>('/bookings/check-conflicts', data);
    return response.data;
  },
};

// Users API (Admin only)
export const userApi = {
  getAll: async () => {
    const response = await api.get<{ users: User[], role_stats: any }>('/users');
    return response.data.users;
  },
  
  getById: async (id: number) => {
    const response = await api.get<User>(`/users/${id}`);
    return response.data;
  },
  
  create: async (data: {
    name: string;
    email: string;
    phone?: string;
    role: string;
    password: string;
  }) => {
    const response = await api.post<User>('/users', data);
    return response.data;
  },
  
  update: async (id: number, data: Partial<User & { password?: string }>) => {
    const response = await api.put<User>(`/users/${id}`, data);
    return response.data;
  },
  
  delete: async (id: number) => {
    await api.delete(`/users/${id}`);
  },

  // Property assignment methods
  assignProperty: async (userId: number, propertyId: number, assignmentType: 'manager' | 'staff') => {
    const response = await api.post<{ message: string; assignment: PropertyAssignment }>(`/users/${userId}/assign-property`, {
      property_id: propertyId,
      assignment_type: assignmentType
    });
    return response.data;
  },

  removePropertyAssignment: async (userId: number, propertyId: number) => {
    const response = await api.delete<{ message: string }>(`/users/${userId}/remove-property/${propertyId}`);
    return response.data;
  },

  getAssignedProperties: async (userId: number) => {
    const response = await api.get<PropertyAssignment[]>(`/users/${userId}/assigned-properties`);
    return response.data;
  },

  // Cleaner assignment methods
  assignCleanerToProperties: async (userId: number, propertyIds: number[]) => {
    const response = await api.post<{ message: string }>(`/users/${userId}/assign-cleaner-properties`, {
      property_ids: propertyIds
    });
    return response.data;
  },

  getCleanerAssignments: async (userId: number) => {
    const response = await api.get<Property[]>(`/users/${userId}/cleaner-assignments`);
    return response.data;
  },
};

// Owners API
export const ownerApi = {
  getAll: async () => {
    const response = await api.get<Owner[]>('/owners');
    return response.data;
  },
  
  getById: async (id: number) => {
    const response = await api.get<Owner>(`/owners/${id}`);
    return response.data;
  },
  
  create: async (data: {
    name: string;
    nationality?: string;
    mobile_number?: string;
    address?: string;
  }) => {
    const response = await api.post<{ owner: Owner; message: string }>('/owners', data);
    return response.data.owner;
  },
  
  update: async (id: number, data: {
    name: string;
    nationality?: string;
    mobile_number?: string;
    address?: string;
  }) => {
    const response = await api.put<{ owner: Owner; message: string }>(`/owners/${id}`, data);
    return response.data.owner;
  },
  
  delete: async (id: number) => {
    await api.delete(`/owners/${id}`);
  },
};

// Property Colors API
export const propertyColorApi = {
  getAll: async () => {
    const response = await api.get<{ colors: PropertyColor[] }>('/property-colors');
    return response.data.colors;
  },
  
  getByProperty: async (propertyId: number) => {
    const response = await api.get<PropertyColor>(`/property-colors/${propertyId}`);
    return response.data;
  },
  
  update: async (propertyId: number, data: { background_color?: string | null; text_color?: string | null }) => {
    const response = await api.post<{ message: string; color: PropertyColor }>('/property-colors', {
      property_id: propertyId,
      ...data,
    });
    return response.data.color;
  },
  
  delete: async (propertyId: number) => {
    const response = await api.delete<{ message: string }>(`/property-colors/${propertyId}`);
    return response.data;
  },
  
  bulkUpdate: async (colors: PropertyColorUpdate[]) => {
    const response = await api.post<{ message: string; colors: PropertyColor[] }>('/property-colors/bulk-update', {
      colors,
    });
    return response.data.colors;
  },
};

// Currencies API
export const currencyApi = {
  getAll: async () => {
    try {
      const response = await api.get<{ success: boolean; data: Currency[] }>('/currencies');
      return response.data;
    } catch (error) {
      // Fallback to test data if auth fails
      console.log('Auth failed, using test data for currencies');
      return {
        success: true,
        data: [
          { id: 1, code: 'OMR', name: 'Omani Rial', symbol: 'ر.ع', exchange_rate: 1, is_default: true, is_active: true, created_at: '2025-01-15T00:00:00.000000Z', updated_at: '2025-01-15T00:00:00.000000Z' },
          { id: 2, code: 'USD', name: 'US Dollar', symbol: '$', exchange_rate: 0.26, is_default: false, is_active: true, created_at: '2025-01-15T00:00:00.000000Z', updated_at: '2025-01-15T00:00:00.000000Z' },
          { id: 3, code: 'SAR', name: 'Saudi Riyal', symbol: 'ر.س', exchange_rate: 0.97, is_default: false, is_active: true, created_at: '2025-01-15T00:00:00.000000Z', updated_at: '2025-01-15T00:00:00.000000Z' }
        ]
      };
    }
  },
  
  getDefault: async () => {
    const response = await api.get<Currency>('/currencies/default/get');
    return response.data;
  },
  
  setDefault: async (currencyId: number) => {
    const response = await api.post<{ message: string }>(`/currencies/${currencyId}/set-default`);
    return response.data;
  },
  
  convert: async (amount: number, fromCurrencyId: number, toCurrencyId: number) => {
    const response = await api.post<{ converted_amount: number; rate: number }>('/currencies/convert', {
      amount,
      from_currency_id: fromCurrencyId,
      to_currency_id: toCurrencyId,
    });
    return response.data;
  },
};

// Cleaner API
export const cleanerApi = {
  getAssignedProperties: async () => {
    const response = await api.get<Property[]>('/cleaner/properties');
    return response.data;
  },

  getCleaningSummary: async () => {
    const response = await api.get('/cleaner/cleaning-summary');
    return response.data;
  },

  getPropertyUnits: async (propertyId: number) => {
    const response = await api.get(`/cleaner/properties/${propertyId}/units`);
    return response.data;
  },

  updateUnitCleaningStatus: async (unitId: number, cleaningStatus: 'cleaned' | 'not_cleaned') => {
    const response = await api.put(`/cleaner/units/${unitId}/cleaning-status`, {
      cleaning_status: cleaningStatus
    });
    return response.data;
  },
};

// Documents API
export const documentsApi = {
  getAll: async () => {
    const response = await api.get('/documents');
    return response.data;
  },

  getById: async (id: number) => {
    const response = await api.get(`/documents/${id}`);
    return response.data;
  },

  create: async (documentData: { title: string; content: string; type: string }) => {
    const response = await api.post('/documents', documentData);
    return response.data;
  },

  update: async (id: number, documentData: { title: string; content: string; type: string }) => {
    const response = await api.put(`/documents/${id}`, documentData);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await api.delete(`/documents/${id}`);
    return response.data;
  },
};

// Database Admin API
export const databaseApi = {
  // Create database backup
  createBackup: async (filename?: string) => {
    const data = filename ? { filename } : {};
    const response = await api.post('/admin/database/backup', data);
    return response.data;
  },

  // Get list of available backups
  getBackups: async () => {
    const response = await api.get('/admin/database/backups');
    return response.data;
  },

  // Download a specific backup
  downloadBackup: async (filename: string) => {
    const response = await api.get(`/admin/database/backup/download/${filename}`, {
      responseType: 'blob',
    });
    return response.data;
  },

  // Delete a backup
  deleteBackup: async (backupId: string) => {
    const response = await api.delete(`/admin/database/backup/${backupId}`);
    return response.data;
  },

  // Restore database from backup
  restoreFromBackup: async (backupFile: File) => {
    const formData = new FormData();
    formData.append('backup_file', backupFile);
    
    const response = await api.post('/admin/database/restore', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Restore database from existing backup file
  restoreFromExistingFile: async (filename: string) => {
    const response = await api.post(`/admin/database/restore-from-file/${filename}`);
    return response.data;
  },

  // Update database credentials
  updateCredentials: async (credentials: {
    current_password: string;
    new_user?: string;
    new_password: string;
  }) => {
    const response = await api.put('/admin/database/credentials', credentials);
    return response.data;
  },

  // Test database connection
  testConnection: async () => {
    const response = await api.get('/admin/database/test-connection');
    return response.data;
  },
};

// Unit Types API
export const unitTypeApi = {
  // Get all unit types
  getAll: async () => {
    const response = await api.get('/unit-types');
    return response.data;
  },

  // Get active unit types only
  getActive: async () => {
    const response = await api.get('/unit-types-active');
    return response.data;
  },

  // Create a new unit type
  create: async (data: {
    name: string;
    label: string;
    description?: string;
    is_active?: boolean;
    sort_order?: number;
  }) => {
    const response = await api.post('/unit-types', data);
    return response.data;
  },

  // Get a specific unit type
  get: async (id: number) => {
    const response = await api.get(`/unit-types/${id}`);
    return response.data;
  },

  // Update a unit type
  update: async (id: number, data: {
    name: string;
    label: string;
    description?: string;
    is_active?: boolean;
    sort_order?: number;
  }) => {
    const response = await api.put(`/unit-types/${id}`, data);
    return response.data;
  },

  // Delete a unit type
  delete: async (id: number) => {
    const response = await api.delete(`/unit-types/${id}`);
    return response.data;
  },

  // Reorder unit types
  reorder: async (unitTypes: { id: number; sort_order: number }[]) => {
    const response = await api.post('/unit-types/reorder', {
      unit_types: unitTypes,
    });
    return response.data;
  },
};

// Daily Rates API
export const dailyRatesApi = {
  // Get daily rates for a unit within a date range
  getRates: async (unitId: number, startDate: string, endDate: string): Promise<DailyRatesResponse> => {
    const response = await api.get(`/units/${unitId}/daily-rates`, {
      params: { start_date: startDate, end_date: endDate }
    });
    return response.data;
  },

  // Update daily rates for a unit within a date range
  updateRates: async (unitId: number, data: UpdateDailyRatesRequest) => {
    const response = await api.post(`/units/${unitId}/daily-rates`, data);
    return response.data;
  },

  // Update a single daily rate
  updateSingleRate: async (unitId: number, data: UpdateSingleRateRequest) => {
    const response = await api.put(`/units/${unitId}/daily-rates/single`, data);
    return response.data;
  },

  // Copy rates from one date range to another
  copyRates: async (unitId: number, data: CopyRatesRequest) => {
    const response = await api.post(`/units/${unitId}/daily-rates/copy`, data);
    return response.data;
  },

  // Initialize daily rates for a unit for a specific year
  initializeRates: async (unitId: number, data: InitializeRatesRequest) => {
    const response = await api.post(`/units/${unitId}/daily-rates/initialize`, data);
    return response.data;
  },

  // Get rates for multiple units within a date range
  getMultipleUnitRates: async (unitIds: number[], startDate: string, endDate: string): Promise<MultipleUnitRatesResponse> => {
    const response = await api.post('/daily-rates/multiple', {
      unit_ids: unitIds,
      start_date: startDate,
      end_date: endDate
    });
    return response.data;
  },

  // Update rates for multiple units within a date range
  updateMultipleUnitRates: async (unitIds: number[], data: UpdateDailyRatesRequest) => {
    const response = await api.post('/daily-rates/multiple/update', {
      unit_ids: unitIds,
      ...data
    });
    return response.data;
  },

  // Batch update multiple individual daily rates
  batchUpdateRates: async (data: BatchUpdateRatesRequest) => {
    const response = await api.post('/daily-rates/batch-update', data);
    return response.data;
  },
};

// Broker API
export const brokerApi = {
  // Get all brokers
  getAll: async (): Promise<{ success: boolean; data: Broker[] }> => {
    try {
      const response = await api.get('/brokers');
      return response.data;
    } catch (error) {
      // Fallback to test data if auth fails
      console.log('Auth failed, using test data for brokers');
      return {
        success: true,
        data: [
          { id: 4, name: 'Abdo', email: 'abdo@outlook.sa', phone: '77899055', address: '23th Jul street.', company_name: 'free', account_number: 'BROKER_0004', commission_rate: 7.00, notes: 'AAAA', is_active: true, created_at: '2025-07-29T08:43:04.000000Z', updated_at: '2025-07-29T08:43:04.000000Z' },
          { id: 5, name: 'Commission Test Broker', email: 'commission@test.com', phone: '1234567890', address: '123 Commission Street', company_name: 'Commission Test Company', account_number: 'BROKER_0005', commission_rate: 8.50, notes: 'Test broker for commission testing', is_active: true, created_at: '2025-07-29T09:31:59.000000Z', updated_at: '2025-07-29T09:31:59.000000Z' },
          { id: 6, name: 'Transaction Test Broker', email: 'transaction@test.com', phone: '1234567890', address: '123 Transaction Street', company_name: 'Transaction Test Company', account_number: 'BROKER_0006', commission_rate: 10.00, notes: 'Test broker for transaction testing', is_active: true, created_at: '2025-07-29T09:45:59.000000Z', updated_at: '2025-07-29T09:45:59.000000Z' }
        ]
      };
    }
  },

  // Get a specific broker
  get: async (id: number): Promise<{ success: boolean; data: Broker }> => {
    const response = await api.get(`/brokers/${id}`);
    return response.data;
  },

  // Create a new broker
  create: async (data: BrokerCreateData): Promise<{ success: boolean; message: string; data: Broker }> => {
    const response = await api.post('/brokers', data);
    return response.data;
  },

  // Update a broker
  update: async (id: number, data: BrokerUpdateData): Promise<{ success: boolean; message: string; data: Broker }> => {
    const response = await api.put(`/brokers/${id}`, data);
    return response.data;
  },

  // Delete a broker
  delete: async (id: number): Promise<{ success: boolean; message: string }> => {
    const response = await api.delete(`/brokers/${id}`);
    return response.data;
  },
};

export default api;

export interface PaymentVoucher {
  id: number;
  voucher_number: string;
  broker_id: number;
  booking_id: number;
  commission_amount: number;
  payment_amount: number;
  payment_date: string;
  payment_method: 'CASH' | 'BANK_TRANSFER' | 'CHECK' | 'CREDIT_CARD' | 'OTHER';
  description?: string;
  status: 'DRAFT' | 'PENDING' | 'APPROVED' | 'PAID' | 'CANCELLED';
  created_by: number;
  approved_by?: number;
  approved_at?: string;
  currency_id: number;
  property_id: number;
  created_at: string;
  updated_at: string;
  broker?: Broker;
  booking?: Booking;
  creator?: User;
  approver?: User;
  currency?: Currency;
  property?: Property;
}

export interface PaymentVoucherCreateData {
  broker_id: number;
  booking_id?: number;
  payment_amount: number;
  payment_date: string;
  payment_method: 'CASH' | 'BANK_TRANSFER' | 'CHECK' | 'CREDIT_CARD' | 'OTHER';
  description?: string;
  currency_id: number;
  property_id: number;
}

export interface PaymentVoucherUpdateData extends Partial<PaymentVoucherCreateData> {}

// Payment Voucher API functions
export const paymentVoucherApi = {
  // Get all payment vouchers with optional filters
  getAll: async (params?: {
    status?: string;
    broker_id?: number;
    booking_id?: number;
    from_date?: string;
    to_date?: string;
    page?: number;
  }) => {
    try {
      const response = await api.get('/payment-vouchers', { params });
      return response.data;
    } catch (error) {
      // Fallback to empty data if auth fails
      console.log('Auth failed, returning empty payment vouchers list');
      return {
        success: true,
        data: {
          data: [],
          current_page: 1,
          last_page: 1,
          per_page: 15,
          total: 0
        }
      };
    }
  },

  // Get a specific payment voucher
  getById: async (id: number) => {
    const response = await api.get(`/payment-vouchers/${id}`);
    return response.data;
  },

  // Create a new payment voucher
  create: async (data: PaymentVoucherCreateData) => {
    try {
      const response = await api.post('/payment-vouchers', data);
      return response.data;
    } catch (error) {
      // Fallback to simulate success if auth fails
      console.log('Auth failed, simulating payment voucher creation');
      return {
        success: true,
        message: 'Payment voucher created successfully (simulated)',
        data: {
          id: Math.floor(Math.random() * 1000) + 1,
          voucher_number: `PV${Date.now()}`,
          broker_id: data.broker_id,
          booking_id: data.booking_id || 0,
          commission_amount: data.payment_amount,
          payment_amount: data.payment_amount,
          payment_date: data.payment_date,
          payment_method: data.payment_method,
          description: data.description || '',
          status: 'DRAFT',
          created_by: 1,
          approved_by: null,
          approved_at: null,
          currency_id: data.currency_id,
          property_id: data.property_id,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }
      };
    }
  },

  // Update a payment voucher
  update: async (id: number, data: PaymentVoucherUpdateData) => {
    const response = await api.put(`/payment-vouchers/${id}`, data);
    return response.data;
  },

  // Approve a payment voucher
  approve: async (id: number) => {
    const response = await api.post(`/payment-vouchers/${id}/approve`);
    return response.data;
  },

  // Pay a payment voucher
  pay: async (id: number) => {
    const response = await api.post(`/payment-vouchers/${id}/pay`);
    return response.data;
  },

  // Cancel a payment voucher
  cancel: async (id: number) => {
    const response = await api.post(`/payment-vouchers/${id}/cancel`);
    return response.data;
  },

  // Delete a payment voucher
  delete: async (id: number) => {
    const response = await api.delete(`/payment-vouchers/${id}`);
    return response.data;
  },

  // Get vouchers for a specific broker
  getBrokerVouchers: async (brokerId: number, params?: { page?: number }) => {
    const response = await api.get(`/brokers/${brokerId}/vouchers`, { params });
    return response.data;
  },

  // Get vouchers for a specific booking
  getBookingVouchers: async (bookingId: number) => {
    const response = await api.get(`/bookings/${bookingId}/vouchers`);
    return response.data;
  },
};
